import Card from '../../components/Card';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useT } from '../../utils/useTranslation';
import api, { DB_TYPE } from '../../api';
import { db } from '../../firebase';
import { getDocs, collection, doc, updateDoc, addDoc, deleteDoc } from 'firebase/firestore';
import Pagination from '../../components/Pagination';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';

const ITEMS_PER_PAGE = 8;

const GENRES = [
  "Shairi (Poetry)", "Afsana", "Novel", "Tanqeed", "History", "Biography", "Children Literature", "Latifiyat"
];

const EMPTY_BOOK = {
  title_en: '',
  title_sd: '',
  author_en: '',
  author_sd: '',
  publisher_en: '',
  publisher_sd: '',
  author_id: '',
  genre: '',
  publication_year: '',
  cover_image_url: '',
  pdf_url: '',
};

export default function ManageBooks() {
  const t = useT();
  const { language } = useLanguage();
  const [books, setBooks] = useState([]);
  const [members, setMembers] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY_BOOK);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState('my_books');

  const { user } = useAuth();
  const isGlobalAdmin = user?.role === 'main_admin' || user?.role?.startsWith('center_body_');
  const isDistCoord = user?.role === 'district_coordinator';
  const isBranchAdmin = user?.role === 'branch_admin' || user?.role === 'branch_secretary_general';
  const isMemberOnly = user?.role === 'member';
  const canPerformActions = isGlobalAdmin || isBranchAdmin || isDistCoord;

  const load = async () => {
    setLoading(true);
    if (DB_TYPE === 'firebase') {
      try {
        const snap = await getDocs(collection(db, 'published_books'));
        let allBooks = snap.docs.map(d => ({ _id: d.id, ...d.data() }));
        setBooks(allBooks);

        const memberSnap = await getDocs(collection(db, 'members'));
        setMembers(memberSnap.docs.map(d => ({ _id: d.id, ...d.data() })));
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    } else {
      api.get('/books').then(r => {
        let allBooks = Array.isArray(r.data) ? r.data : [];
        setBooks(allBooks);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleFileUpload = (file, field) => {
    if (!file) return;

    // 700KB limit to ensure Base64 doesn't exceed Firestore 1MB document limit
    if (file.size > 700 * 1024) {
      const msg = field === 'cover_image_url'
        ? 'Image is too large (limit 700KB)'
        : 'PDF is too large (limit 700KB)';
      toast.error(msg);
      return;
    }

    const reader = new FileReader();
    reader.onload = e => {
      set(field, e.target.result);
      toast.success(field === 'cover_image_url' ? 'Cover image selected' : 'Book file selected');
    };
    reader.readAsDataURL(file);
  };

  const openView = (b) => {
    setForm(b);
    setEditId(b._id);
    setModal(true);
  };

  const [saving, setSaving] = useState(false);

  const save = async e => {
    e.preventDefault();
    if (saving) return;

    // Validation
    if (!form.title_en || !form.title_sd) {
      toast.error('Title is required in both English and Sindhi');
      return;
    }
    if (!form.author_en || !form.author_sd) {
      toast.error('Author name is required in both English and Sindhi');
      return;
    }
    if (!form.genre) {
      toast.error('Please select a genre');
      return;
    }

    const payloadSize = JSON.stringify(form).length;
    if (DB_TYPE === 'firebase' && payloadSize > 950000) {
      toast.error('Total book data exceeds limit. Please use smaller files.');
      return;
    }

    setSaving(true);
    console.log('Saving book with payload size:', payloadSize);

    try {
      const payload = {
        ...form,
        publication_year: parseInt(form.publication_year) || new Date().getFullYear()
      };
      if (DB_TYPE === 'firebase') {
        if (editId) {
          await updateDoc(doc(db, 'published_books', editId), payload);
          toast.success('Updated successfully');
        } else {
          if (!user?.id) throw new Error('User not authenticated');
          await addDoc(collection(db, 'published_books'), {
            ...payload,
            addedBy: user.id,
            branchId: user.branchId || '',
            createdAt: new Date().toISOString()
          });
          toast.success('Book added successfully');
        }
      } else {
        if (editId) {
          await api.put(`/books/${editId}`, payload);
        } else {
          await api.post('/books', payload);
        }
        toast.success('Success');
      }
      setModal(false);
      load();
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Error saving book: ' + (err.message || 'Check console'));
    } finally {
      setSaving(false);
    }
  };

  const del = async id => {
    if (!window.confirm('Are you sure?')) return;
    try {
      if (DB_TYPE === 'firebase') {
        await deleteDoc(doc(db, 'published_books', id));
      } else {
        await api.delete(`/books/${id}`);
      }
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Error');
    }
  };

  const filtered = books.filter(b => {
    const matchesSearch = (b.title_en || '').toLowerCase().includes(search.toLowerCase()) ||
      (b.title_sd || '').toLowerCase().includes(search.toLowerCase()) ||
      (b.author_en || '').toLowerCase().includes(search.toLowerCase()) ||
      (b.author_sd || '').toLowerCase().includes(search.toLowerCase());

    if (!matchesSearch) return false;
    if (isMemberOnly || isGlobalAdmin) return true;

    if (tab === 'my_books') {
      return b.addedBy === user?.id;
    } else {
      return b.addedBy !== user?.id;
    }
  });

  const isRTL = language === 'sindhi';
  const renderMemberName = (m) => isRTL ? (m.full_name_sindhi || m.full_name) : m.full_name;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 32, gap: 24, flexWrap: 'wrap', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
        <div>
          <h1 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: isRTL ? 42 : 32, fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 4 }}>
            {isMemberOnly ? t('books') : t('manageBooks')}
          </h1>
          <p style={{ fontSize: 14, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
            {t('books_count', { count: books.length })}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          <div style={{ position: 'relative', width: 250 }}>
            <span style={{ position: 'absolute', [isRTL ? 'right' : 'left']: 16, top: '50%', transform: 'translateY(-50%)', opacity: 0.5 }}>🔍</span>
            <input className="form-input" placeholder={t('search')} style={{ width: '100%', paddingLeft: isRTL ? 16 : 44, paddingRight: isRTL ? 44 : 16, height: 48, borderRadius: 14, border: '1px solid var(--sindhi-cream)', background: 'white', fontSize: 14, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {canPerformActions && (
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="btn btn-primary" style={{ height: 48, padding: '0 24px', borderRadius: 12, fontSize: 14, fontWeight: 800, background: 'var(--sindhi-maroon)', border: 'none', color: 'white', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => { setForm(EMPTY_BOOK); setEditId(null); setModal(true); }}>
              {t('Add New Book')}
            </motion.button>
          )}
        </div>
      </div>

      {!isGlobalAdmin && !isMemberOnly && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, background: 'var(--sindhi-cream)', padding: 6, borderRadius: 16, width: 'fit-content', direction: isRTL ? 'rtl' : 'ltr' }}>
          {[
            ['my_books', t('myAddedBooks')],
            ['all_books', t('otherBooks')]
          ].map(([v, l]) => (
            <button key={v} className={`btn-tab ${tab === v ? 'active' : ''}`} style={{ padding: '10px 24px', borderRadius: 12, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer', background: tab === v ? 'white' : 'transparent', color: tab === v ? 'var(--sindhi-navy)' : 'var(--sindhi-text-muted)', boxShadow: tab === v ? '0 4px 12px rgba(0,0,0,0.05)' : 'none', transition: 'all 0.2s', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => { setTab(v); setPage(1); }}>
              {l}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <div className="spinner" />
        </div>
      ) : (
        <Card style={{ overflow: 'hidden', border: '1px solid var(--sindhi-cream)', background: 'white' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', borderCollapse: 'collapse', direction: isRTL ? 'rtl' : 'ltr' }}>
              <thead>
                <tr style={{ background: 'var(--sindhi-ivory)', borderBottom: '1px solid var(--sindhi-cream)' }}>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('book')}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('author')}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('publisher')}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('genre')}</th>
                  {(canPerformActions || isMemberOnly) && <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isMemberOnly ? t('details') : t('actions')}</th>}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence mode="popLayout">
                  {filtered.length === 0 ? (
                    <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <td colSpan={5} style={{ textAlign: 'center', padding: 80, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                        {t('No Books Found')}
                      </td>
                    </motion.tr>
                  ) : (
                    filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map((b) => {
                      const isOwner = b.addedBy === user?.id;
                      const canEdit = isGlobalAdmin || isOwner;
                      return <motion.tr key={b._id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} onClick={() => (isMemberOnly || (!isGlobalAdmin && tab === 'all_books')) && openView(b)} style={{ borderBottom: '1px solid var(--sindhi-cream)', cursor: (isMemberOnly || (!isGlobalAdmin && tab === 'all_books')) ? 'pointer' : 'default' }} whileHover={(isMemberOnly || (!isGlobalAdmin && tab === 'all_books')) ? { backgroundColor: 'var(--sindhi-ivory)' } : {}}>
                        <td style={{ padding: '20px 24px' }}>
                          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                            <div style={{ width: 48, height: 64, background: 'var(--sindhi-ivory)', borderRadius: 6, overflow: 'hidden', flexShrink: 0, border: '1px solid var(--sindhi-cream)' }}>
                              {b.cover_image_url && <img src={b.cover_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                            </div>
                            <div style={{ textAlign: isRTL ? 'right' : 'left' }}>
                              <div style={{ fontWeight: 800, color: 'var(--sindhi-navy)', fontSize: isRTL ? 18 : 15, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? (b.title_sd || b.title_en) : b.title_en}</div>
                              {!isRTL && b.title_sd && <div style={{ fontSize: 14, color: 'var(--sindhi-maroon)', fontFamily: 'var(--font-sindhi)', direction: 'rtl' }}>{b.title_sd}</div>}
                              {isRTL && b.title_en && <div style={{ fontSize: 12, color: 'var(--sindhi-text-muted)' }}>{b.title_en}</div>}
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '20px 24px' }}>
                          <div style={{ fontWeight: 700, color: 'var(--sindhi-navy)', fontSize: isRTL ? 16 : 14, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? (b.author_sd || b.author_en) : b.author_en}</div>
                          <div style={{ fontSize: 12, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? b.author_en : b.author_sd}</div>
                        </td>
                        <td style={{ padding: '20px 24px' }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? (b.publisher_sd || b.publisher_en) : b.publisher_en}</div>
                          <div style={{ fontSize: 12, color: 'var(--sindhi-maroon)', fontWeight: 800 }}>{b.publication_year}</div>
                        </td>
                        <td style={{ padding: '20px 24px' }}>
                          <span style={{ padding: '6px 12px', background: 'var(--sindhi-ivory)', borderRadius: 8, fontSize: 11, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase' }}>{b.genre}</span>
                        </td>
                        {(canPerformActions || isMemberOnly) && (
                          <td style={{ padding: '20px 24px' }}>
                            <div style={{ display: 'flex', gap: 8, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                              {isMemberOnly || (!isGlobalAdmin && tab === 'all_books') ? (
                                <button className="btn-action" style={{ background: 'var(--sindhi-navy)', color: 'white', padding: '6px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer', border: 'none' }} onClick={() => openView(b)}>{t('details')}</button>
                              ) : canEdit ? (
                                <>
                                  <button className="btn-action" style={{ background: 'var(--sindhi-ivory)', color: 'var(--sindhi-navy)', border: '1px solid var(--sindhi-cream)', padding: '6px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => { setForm(b); setEditId(b._id); setModal(true); }}>{t('edit')}</button>
                                  <button className="btn-action" style={{ background: '#feecef', color: '#e11d48', border: 'none', padding: '6px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => del(b._id)}>{t('delete')}</button>
                                </>
                              ) : (
                                <span style={{ fontSize: 11, color: 'var(--sindhi-text-muted)' }}>{t('readOnly')}</span>
                              )}
                            </div>
                          </td>
                        )}
                      </motion.tr>
                    })
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
          <div style={{ padding: 24 }}>
            <Pagination page={page} totalPages={Math.ceil(filtered.length / ITEMS_PER_PAGE)} onPageChange={setPage} />
          </div>
        </Card>
      )}

      <AnimatePresence>
        {modal && (
          <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(5px)', position: 'fixed', inset: 0, zIndex: 10000 }} onClick={e => e.target === e.currentTarget && setModal(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="modal-box" style={{ width: 800, maxHeight: 'min(620px, 90vh)', overflow: 'hidden', borderRadius: 24, background: 'white', padding: 0, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '16px 28px', background: 'var(--sindhi-ivory)', borderBottom: '1px solid var(--sindhi-cream)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: isRTL ? 'row-reverse' : 'row', flexShrink: 0 }}>
                <h2 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: isRTL ? 24 : 18, fontWeight: 800, color: 'var(--sindhi-navy)', margin: 0 }}>{isMemberOnly || (!isGlobalAdmin && tab === 'all_books') ? t('bookDetails') : (editId ? t('editBook') : t('addNewBook'))}</h2>
                <button style={{ background: 'white', border: '1px solid var(--sindhi-cream)', width: 30, height: 30, borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setModal(false)}>✕</button>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px' }}>
                {isMemberOnly || (!isGlobalAdmin && tab === 'all_books') ? (
                  <div style={{ display: 'flex', gap: 28, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                    <div style={{ width: 200, height: 280, background: 'var(--sindhi-ivory)', borderRadius: 16, overflow: 'hidden', border: '1px solid var(--sindhi-cream)', flexShrink: 0, boxShadow: '0 10px 20px rgba(0,0,0,0.1)' }}>
                      {form.cover_image_url ? <img src={form.cover_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 50, opacity: 0.1 }}>📚</div>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--sindhi-maroon)', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 8 }}>{form.genre}</div>
                      <h3 style={{ fontSize: 26, fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 4, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', lineHeight: 1.2 }}>{isRTL ? (form.title_sd || form.title_en) : form.title_en}</h3>
                      {form.title_sd && !isRTL && <div style={{ fontSize: 20, color: 'var(--sindhi-maroon)', fontFamily: 'var(--font-sindhi)', marginBottom: 12 }}>{form.title_sd}</div>}
                      {form.title_en && isRTL && <div style={{ fontSize: 14, color: 'var(--sindhi-text-muted)', marginBottom: 12 }}>{form.title_en}</div>}

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, marginTop: 32, background: 'var(--sindhi-ivory)', padding: 24, borderRadius: 16 }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', opacity: 0.5, textTransform: 'uppercase', marginBottom: 6 }}>{t('author')}</div>
                          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? (form.author_sd || form.author_en) : form.author_en}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', opacity: 0.5, textTransform: 'uppercase', marginBottom: 6 }}>{t('publisher')}</div>
                          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? (form.publisher_sd || form.publisher_en) : form.publisher_en}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', opacity: 0.5, textTransform: 'uppercase', marginBottom: 6 }}>{t('publicationYear')}</div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--sindhi-maroon)' }}>{form.publication_year}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', opacity: 0.5, textTransform: 'uppercase', marginBottom: 6 }}>{t('genre')}</div>
                          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--sindhi-navy)' }}>{form.genre}</div>
                        </div>
                      </div>

                      <div style={{ marginTop: 40, display: 'flex', gap: 16, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                        {form.pdf_url ? (
                          <a href={form.pdf_url} download target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', flex: 1 }}>
                            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={{ width: '100%', height: 56, borderRadius: 14, background: 'var(--sindhi-maroon)', color: 'white', border: 'none', fontSize: 16, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, cursor: 'pointer', boxShadow: '0 8px 24px rgba(126, 34, 34, 0.2)' }}>
                              <span>📥</span> {t('downloadBook')}
                            </motion.button>
                          </a>
                        ) : (
                          <div style={{ flex: 1, height: 56, borderRadius: 14, background: 'var(--sindhi-cream)', color: 'var(--sindhi-text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600 }}>
                            {isRTL ? 'هن وقت فائل موجود ناهي' : 'File not available at the moment'}
                          </div>
                        )}
                        <button onClick={() => setModal(false)} style={{ height: 56, padding: '0 32px', borderRadius: 14, background: 'white', border: '1px solid var(--sindhi-cream)', color: 'var(--sindhi-navy)', fontWeight: 700, cursor: 'pointer' }}>{isRTL ? 'بند ڪريو' : 'Close'}</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={save} style={{ padding: 32 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                        <label className="form-label">{t('titleEnglish')} *</label>
                        <input className="form-input" placeholder="e.g., The Great Sindhi Poets" value={form.title_en} onChange={e => set('title_en', e.target.value)} required />
                      </div>
                      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                        <label className="form-label" style={{ fontFamily: 'var(--font-sindhi)' }}>{t('titleSindhi')} *</label>
                        <input className="form-input" placeholder="مثال طور: عظيم سنڌي شاعر" style={{ fontFamily: 'var(--font-sindhi)', direction: 'rtl' }} value={form.title_sd} onChange={e => set('title_sd', e.target.value)} required />
                      </div>

                      <div className="form-group">
                        <label className="form-label">{t('authorEnglish')} *</label>
                        <input className="form-input" placeholder="e.g., Shah Abdul Latif Bhittai" value={form.author_en} onChange={e => set('author_en', e.target.value)} required />
                      </div>
                      <div className="form-group">
                        <label className="form-label" style={{ fontFamily: 'var(--font-sindhi)' }}>{t('authorSindhi')} *</label>
                        <input className="form-input" placeholder="مثال طور: شاھ عبدالطيف ڀٽائي" style={{ fontFamily: 'var(--font-sindhi)', direction: 'rtl' }} value={form.author_sd} onChange={e => set('author_sd', e.target.value)} required />
                      </div>

                      <div className="form-group">
                        <label className="form-label">{t('publisherEnglish')}</label>
                        <input className="form-input" placeholder="e.g., Sindhi Adabi Board" value={form.publisher_en} onChange={e => set('publisher_en', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label" style={{ fontFamily: 'var(--font-sindhi)' }}>{t('publisherSindhi')}</label>
                        <input className="form-input" placeholder="مثال طور: سنڌي ادبي بورڊ" style={{ fontFamily: 'var(--font-sindhi)', direction: 'rtl' }} value={form.publisher_sd} onChange={e => set('publisher_sd', e.target.value)} />
                      </div>

                      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                        <label className="form-label" style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('linkToMember')} ({t('optional')})</label>
                        <select className="form-input" value={form.author_id} onChange={e => set('author_id', e.target.value)} style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                          <option value="">{t('selectMember')}</option>
                          {members.map(m => <option key={m._id} value={m._id}>{renderMemberName(m)} {m.literary_name ? `(${m.literary_name})` : ''}</option>)}
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label" style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('publicationYear')}</label>
                        <input className="form-input" type="number" placeholder="e.g., 2023" value={form.publication_year} onChange={e => set('publication_year', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label" style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('genre')}</label>
                        <select className="form-input" value={form.genre} onChange={e => set('genre', e.target.value)} style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                          <option value="">{t('selectGenre')}</option>
                          {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label" style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('coverPhoto')}</label>
                        <input className="form-input" type="file" accept="image/*" onChange={e => handleFileUpload(e.target.files[0], 'cover_image_url')} style={{ border: '1px solid #ddd', padding: 10 }} />
                        {form.cover_image_url && <img src={form.cover_image_url} style={{ width: 80, height: 110, marginTop: 15, borderRadius: 8, objectFit: 'cover', border: '2px solid var(--sindhi-cream)' }} alt="" />}
                      </div>
                      <div className="form-group">
                        <label className="form-label" style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('fileAttachment')} (PDF)</label>
                        <input className="form-input" type="file" accept=".pdf" onChange={e => handleFileUpload(e.target.files[0], 'pdf_url')} style={{ border: '1px solid #ddd', padding: 10 }} />
                        {form.pdf_url && <div style={{ marginTop: 10, fontSize: 12, color: '#1da352' }}>✅ PDF Selected</div>}
                      </div>
                    </div>
                    <div style={{ marginTop: 32, display: 'flex', gap: 12, borderTop: '1px solid var(--sindhi-cream)', paddingTop: 24, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                      <button type="submit" disabled={saving} className="btn btn-primary" style={{ height: 50, flex: 1, borderRadius: 12, fontWeight: 800, background: 'var(--sindhi-maroon)', border: 'none', color: 'white', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', opacity: saving ? 0.7 : 1 }}>
                        {saving ? (isRTL ? 'محفوظ ٿي رهيو آهي...' : 'Saving...') : (editId ? t('saveChanges') : t('Submit Book'))}
                      </button>
                      <button type="button" onClick={() => setModal(false)} className="btn btn-outline" style={{ height: 50, padding: '0 32px', borderRadius: 12, fontWeight: 800, border: '1px solid var(--sindhi-cream)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                        {t('cancel')}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
